import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Button, TextField, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Alert,
} from '@mui/material';
import { SearchRounded, DownloadRounded, ReceiptLongRounded } from '@mui/icons-material';
import { colors } from '../../theme/theme';
import { usePaymentHistory, PaymentHistoryItem, PaymentStatus } from '../../hooks/usePaymentHistory';
import { rs, fmtDate } from './billingUtils';

const STATUS_STYLE: Record<PaymentStatus, { bg: string; fg: string; label: string }> = {
  successful: { bg: '#ECFDF5', fg: '#059669', label: 'Successful' },
  failed: { bg: '#FEF2F2', fg: '#B91C1C', label: 'Failed' },
  pending: { bg: '#FFFBEB', fg: '#92400E', label: 'Pending' },
};

const PaymentsPage: React.FC = () => {
  const { payments, isLoading, isDownloading, error, getPaymentHistory, downloadInvoice } = usePaymentHistory();
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => { getPaymentHistory(); }, [getPaymentHistory]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return payments;
    return payments.filter((p) =>
      [p.reference_id, p.invoice_id || '', fmtDate(p.payment_date)].join(' ').toLowerCase().includes(q)
    );
  }, [payments, search]);

  const onDownload = async (p: PaymentHistoryItem) => {
    setDownloadingId(p.id);
    try { await downloadInvoice(p.id); } finally { setDownloadingId(null); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField size="small" placeholder="Search by reference, invoice or date" value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }} sx={{ width: 320 }} />
        <Typography sx={{ alignSelf: 'center', fontSize: 13, color: colors.textSecondary }}>{visible.length} transaction{visible.length === 1 ? '' : 's'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px', overflow: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box sx={{ width: 72, height: 72, borderRadius: '20px', bgcolor: colors.primaryLight, color: colors.primary, display: 'grid', placeItems: 'center', mx: 'auto', mb: 1.5 }}><ReceiptLongRounded sx={{ fontSize: 32 }} /></Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>No transactions found</Typography>
            <Typography color="text.secondary">{search ? 'No transactions match your search.' : 'No payment history available yet.'}</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payment date</TableCell><TableCell>Amount</TableCell><TableCell>Providers</TableCell>
                <TableCell>Tokens</TableCell><TableCell>Status</TableCell><TableCell>Reference ID</TableCell><TableCell>Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((p) => {
                const st = STATUS_STYLE[p.status];
                return (
                  <TableRow key={p.id} hover>
                    <TableCell>{fmtDate(p.payment_date)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{rs(p.amount)}</TableCell>
                    <TableCell>{p.provider_count}</TableCell>
                    <TableCell>{p.token_count.toLocaleString('en-IN')}</TableCell>
                    <TableCell><Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 600, height: 24 }} /></TableCell>
                    <TableCell><code style={{ fontSize: 12.5, color: colors.textTertiary }}>{p.reference_id}</code></TableCell>
                    <TableCell>
                      {p.status === 'successful' && p.invoice_id ? (
                        <Button size="small" variant="text" startIcon={downloadingId === p.id ? <CircularProgress size={14} /> : <DownloadRounded />} disabled={isDownloading} onClick={() => onDownload(p)}>
                          {p.invoice_id}
                        </Button>
                      ) : <Typography sx={{ color: colors.textTertiary, fontSize: 13 }}>—</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};

export default PaymentsPage;
